import React from 'react';

// Performance monitoring utilities

export const measurePerformance = (name: string, fn: () => void | Promise<void>) => {
  const start = performance.now();
  
  const result = fn();
  
  if (result instanceof Promise) {
    return result.finally(() => {
      const end = performance.now();
      console.log(`⏱️ ${name} took ${(end - start).toFixed(2)}ms`);
    });
  } else {
    const end = performance.now();
    console.log(`⏱️ ${name} took ${(end - start).toFixed(2)}ms`);
    return result;
  }
};

export const measureAsyncPerformance = async (name: string, fn: () => Promise<any>) => {
  const start = performance.now();
  try {
    const result = await fn();
    const end = performance.now();
    console.log(`⏱️ ${name} took ${(end - start).toFixed(2)}ms`);
    return result;
  } catch (error) {
    const end = performance.now();
    console.error(`❌ ${name} failed after ${(end - start).toFixed(2)}ms:`, error);
    throw error;
  }
};

// Component render performance monitoring
export const withPerformanceMonitoring = <P extends object>(
  Component: React.ComponentType<P>,
  componentName: string
) => {
  return React.memo((props: P) => {
    const renderStart = performance.now();
    
    React.useEffect(() => {
      const renderEnd = performance.now();
      console.log(`🎨 ${componentName} rendered in ${(renderEnd - renderStart).toFixed(2)}ms`);
    });
    
    return React.createElement(Component, props);
  });
};

// API call performance monitoring
export const monitorApiCall = async <T>(
  apiCall: () => Promise<T>,
  endpoint: string
): Promise<T> => {
  return measureAsyncPerformance(`API Call: ${endpoint}`, apiCall);
};

// Memory usage monitoring
export const logMemoryUsage = (label: string = 'Memory Usage') => {
  if ('memory' in performance) {
    const memory = (performance as any).memory;
    console.log(`🧠 ${label}:`, {
      used: `${(memory.usedJSHeapSize / 1024 / 1024).toFixed(2)}MB`,
      total: `${(memory.totalJSHeapSize / 1024 / 1024).toFixed(2)}MB`,
      limit: `${(memory.jsHeapSizeLimit / 1024 / 1024).toFixed(2)}MB`
    });
  }
};

// Bundle size monitoring (development only)
export const logBundleInfo = () => {
  if (process.env.NODE_ENV === 'development') {
    console.log('📦 Bundle Info:', {
      environment: process.env.NODE_ENV,
      buildTime: new Date().toISOString(),
      userAgent: navigator.userAgent
    });
  }
};
